/**
 * Utility to schedule work during browser idle time with fallback
 * Uses requestIdleCallback when available, setTimeout as fallback
 * @author AI Assistant
 * @date 2026-01-07
 */

/**
 * Execute a callback during browser idle time or after timeout
 * @param fn - Function to execute
 * @param timeout - Maximum time to wait before forcing execution (ms)
 */
export function requestIdleOrTimeout(fn: () => void, timeout = 2000): void {
  const ric = (window as any).requestIdleCallback;
  if (typeof ric === 'function') {
    ric(fn, { timeout });
  } else {
    // Fallback for browsers without requestIdleCallback (Safari < 16)
    setTimeout(fn, 0);
  }
}

/**
 * Execute an async callback during browser idle time
 * @param fn - Async function to execute
 * @param timeout - Maximum time to wait before forcing execution (ms)
 */
export function requestIdleOrTimeoutAsync(fn: () => Promise<void>, timeout = 2000): void {
  requestIdleOrTimeout(() => {
    void fn().catch(err => {
      console.error('[idle] Background task failed:', err);
    });
  }, timeout);
}

/**
 * Safe performance.mark wrapper that checks for performance API availability
 * Prevents errors in SSR/test environments where performance may be undefined
 * @param markName - Name of the performance mark
 */
export function perfMark(markName: string): void {
  if (typeof performance !== 'undefined' && performance.mark) {
    performance.mark(markName);
  }
}

/**
 * Safe performance.measure wrapper that checks for performance API availability
 * Prevents errors in SSR/test environments where performance may be undefined
 * @param measureName - Name of the performance measure
 * @param startMark - Start mark name
 * @param endMark - End mark name
 */
export function perfMeasure(measureName: string, startMark: string, endMark: string): void {
  if (typeof performance !== 'undefined' && performance.measure) {
    try {
      performance.measure(measureName, startMark, endMark);
    } catch (e) {
      /* marks may not exist */
    }
  }
}
